import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api';
import { Card, CardHeader, CardTitle } from '../../components/ui/Card';

interface MetricsData {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  totalLeads: number;
  tenantsByRegion: {
    ksa: number;
    eu: number;
    us: number;
  };
  tenantsByPlan: {
    free: number;
    starter: number;
    pro: number;
    enterprise: number;
  };
  leadsByStage: {
    lead: number;
    trial: number;
    active: number;
    at_risk: number;
    churned: number;
  };
}

export function PlatformMetrics() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = !!user?.isPlatformAdmin;

  useEffect(() => {
    if (!isAdmin) return;
    fetchMetrics();
  }, [isAdmin]);

  async function fetchMetrics() {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<MetricsData>(
      '/api/admin/metrics'
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setMetrics(data);
    }
    setLoading(false);
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">
            Access Denied
          </h1>
          <p className="text-slate-400">
            You do not have permission to view this page. Platform admin access is required.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Platform Metrics
          </h1>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const maxRegion = Math.max(
    metrics.tenantsByRegion.ksa,
    metrics.tenantsByRegion.eu,
    metrics.tenantsByRegion.us,
    1
  );

  const maxPlan = Math.max(
    metrics.tenantsByPlan.free,
    metrics.tenantsByPlan.starter,
    metrics.tenantsByPlan.pro,
    metrics.tenantsByPlan.enterprise,
    1
  );

  const maxStage = Math.max(
    metrics.leadsByStage.lead,
    metrics.leadsByStage.trial,
    metrics.leadsByStage.active,
    metrics.leadsByStage.at_risk,
    metrics.leadsByStage.churned,
    1
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">
          Platform Metrics
        </h1>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Tenants" value={metrics.totalTenants} />
        <StatCard label="Active Tenants" value={metrics.activeTenants} />
        <StatCard label="Trial Tenants" value={metrics.trialTenants} />
        <StatCard label="Total Leads" value={metrics.totalLeads} />
      </div>

      {/* Tenants by Region */}
      <Card>
        <CardHeader>
          <CardTitle>Tenants by Region</CardTitle>
        </CardHeader>

        <div className="space-y-4">
          <MetricBar
            label="KSA"
            value={metrics.tenantsByRegion.ksa}
            max={maxRegion}
            color="bg-brand"
          />
          <MetricBar
            label="EU"
            value={metrics.tenantsByRegion.eu}
            max={maxRegion}
            color="bg-blue-500"
          />
          <MetricBar
            label="US"
            value={metrics.tenantsByRegion.us}
            max={maxRegion}
            color="bg-growth"
          />
        </div>
      </Card>

      {/* Tenants by Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Tenants by Plan</CardTitle>
        </CardHeader>

        <div className="space-y-4">
          <MetricBar
            label="Free"
            value={metrics.tenantsByPlan.free}
            max={maxPlan}
            color="bg-slate-500"
          />
          <MetricBar
            label="Starter"
            value={metrics.tenantsByPlan.starter}
            max={maxPlan}
            color="bg-blue-500"
          />
          <MetricBar
            label="Pro"
            value={metrics.tenantsByPlan.pro}
            max={maxPlan}
            color="bg-brand"
          />
          <MetricBar
            label="Enterprise"
            value={metrics.tenantsByPlan.enterprise}
            max={maxPlan}
            color="bg-yellow-500"
          />
        </div>
      </Card>

      {/* Lead Pipeline */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Pipeline</CardTitle>
        </CardHeader>

        <div className="space-y-4">
          <MetricBar
            label="Lead"
            value={metrics.leadsByStage.lead}
            max={maxStage}
            color="bg-blue-500"
          />
          <MetricBar
            label="Trial"
            value={metrics.leadsByStage.trial}
            max={maxStage}
            color="bg-brand"
          />
          <MetricBar
            label="Active"
            value={metrics.leadsByStage.active}
            max={maxStage}
            color="bg-growth"
          />
          <MetricBar
            label="At Risk"
            value={metrics.leadsByStage.at_risk}
            max={maxStage}
            color="bg-yellow-500"
          />
          <MetricBar
            label="Churned"
            value={metrics.leadsByStage.churned}
            max={maxStage}
            color="bg-red-500"
          />
        </div>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-surface-mid rounded-xl p-5 border border-border">
      <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className="text-2xl font-display font-bold text-white">{value}</p>
    </div>
  );
}

function MetricBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  const percent = max > 0 ? (value / max) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-slate-300">{label}</span>
        <span className="text-sm font-medium text-white">{value}</span>
      </div>
      <div className="h-2 bg-surface-dark rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
